<template>
  <div style="margin:20px;">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Daily Dispatch</span><br/>
      <span style="font-style: italic; font-size: 18px">Hale Transportation daily dispatch sheet, split by garage{{ garageListLabel }}, with departing (OUT) and already-out (RETURNING) trips.</span>
    </div>

    <div style="position: relative; font-size: 20px; font-weight: bold;" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_day(-1)">&lt;</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_viewDate" :config="options" :placeholder="details_viewDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_day(1)">&gt;</a>
      &nbsp;&nbsp;|&nbsp;&nbsp;Garage&nbsp;
      <b-form-select style="width: 160px; font-size: 16px;" v-model="garage_filter" :options="garageOptions"/>
      &nbsp;&nbsp;|&nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>
    </div>

    <div style="position: relative; margin: 10px 0; text-align: center; font-size: 15px;">
      <!-- one range/garage selection feeds print, CSV export, AND auto-scroll below --
           there's only ever one "range" on this page, so it isn't labeled "print range"
           anymore. it starts on the coming week and follows the day on its own until
           somebody picks their own dates -- see rollDateRange(). -->
      Range:&nbsp;
      <date-picker style="display:inline-block; width:130px" v-model="print_fromDate" :config="options"/>
      &nbsp;to&nbsp;
      <date-picker style="display:inline-block; width:130px" v-model="print_toDate" :config="options"/>
      &nbsp;&nbsp;<span @click="printPage" style="cursor:pointer" :title="printing ? 'Preparing print...' : 'Print (matches on-screen layout, one sheet per day)'">
        <b-spinner v-if="printing" small style="width: 1rem; height: 1rem;"/>
        <img v-else height="26px" src="/print-icon.png" alt="Print"/>&nbsp;Print
      </span>
      &nbsp;&nbsp;<a href="#" style="font-size: 13px" @click.prevent="downloadCsv" title="Download the range above as a spreadsheet">export CSV</a>
      &nbsp;&nbsp;<a href="#" style="font-size: 13px" @click.prevent="enterScroll" title="Full-screen auto-scrolling display of the range/garage above, for a lounge TV">Auto Scroll</a>
    </div>

    <div v-if="!loading">
      <div v-for="garage in visibleGarages" :key="garage" class="garage-section">
        <div class="garage-header">Garage: {{ garage.toUpperCase() }} ({{ (garageRows[garage] || []).length }})</div>
        <table border="1" class="dispatch-table">
          <tr>
            <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
          </tr>
          <tr v-if="!(garageRows[garage] || []).length">
            <td :colspan="columns.length">No trips</td>
          </tr>
          <tr v-for="(row, idx) in (garageRows[garage] || [])" :key="garage + '-' + idx">
            <td v-for="col in columns" :key="col.key" :class="col.key === 'status' ? statusClass(row.status) : ''">
              {{ row[col.key] }}
            </td>
          </tr>
        </table>
      </div>
    </div>
    <div v-else style="min-height: 400px; display: flex; align-items: center; justify-content: center;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <!-- print stage: renders ONE printPages[] entry at a time, off-screen.
         printPage() below loops through printPages, capturing this single
         page as its own small html2canvas snapshot before moving to the
         next -- every capture stays comfortably within one physical
         letter/landscape page's worth of pixels.

         we used to hand the WHOLE multi-day print range to vue-html2pdf in
         one go, which renders everything into a single giant canvas before
         slicing it into pages. that silently produced an all-blank PDF
         (right page count, zero pixels) once a multi-day range added up to
         enough total height to exceed the browser's max canvas
         width/height -- easy to hit since the print range defaults to the
         rest of the month. capturing page-by-page means the total number
         of days/pages being printed can never blow that limit. -->
    <div
        v-if="printStagePage"
        ref="printStage"
        :style="{ position: 'fixed', left: '-99999px', top: 0, width: printStageWidthPx, background: '#fff', padding: printStagePaddingPx, color: '#000' }"
    >
      <div class="pdf-page-title">Daily Dispatch — {{ printStagePage.date_label }}</div>
      <div v-for="(section, sIdx) in printStagePage.sections" :key="'stage-section-' + sIdx" style="margin-top: 10px;">
        <!-- lighter than the on-screen .garage-header (dark navy bar, fine on a
             monitor) -- a solid dark band prints as a heavy black block and
             eats ink, so print uses a light band with dark text instead. -->
        <div class="garage-header" style="background: #ddd; color: #000;">
          Garage: {{ section.garage.toUpperCase() }} ({{ section.total }}){{ section.continued ? ' — (cont\'d)' : '' }}
        </div>
        <table border="1" class="dispatch-table">
          <tr>
            <th v-for="col in columns" :key="col.key" style="background-color: #F9F8F8;">{{ col.label }}</th>
          </tr>
          <tr v-if="!section.rows.length">
            <td :colspan="columns.length">No trips</td>
          </tr>
          <tr v-for="(row, rIdx) in section.rows" :key="'stage-row-' + rIdx">
            <td v-for="col in columns" :key="col.key" :class="col.key === 'status' ? statusClass(row.status) : ''">
              {{ row[col.key] }}
            </td>
          </tr>
        </table>
      </div>
      <div style="margin-top: 8px; font-size: 11px; text-align: right;">Printed {{ printedAt }}</div>
    </div>

    <!-- full-screen auto-scroll display: pages through scrollPages (same
         garage-packing/(cont'd) rules as print, but packed to the measured
         screen rather than a physical page) for an unattended lounge TV. -->
    <div v-if="scroll_mode" ref="scrollOverlay" class="hale-scroll-overlay" :style="{ fontSize: scrollFontPx + 'px' }">
      <div class="hale-scroll-header">
        <span class="hale-scroll-title">Daily Dispatch — {{ currentScrollPageData ? currentScrollPageData.date_label : '' }}</span>
        <span class="hale-scroll-controls">
          <span v-if="scrollPageCount > 1" class="hale-scroll-count">{{ scroll_page + 1 }} / {{ scrollPageCount }}</span>
          <a href="#" class="hale-scroll-exit" @click.prevent="exitScroll">Exit</a>
        </span>
      </div>
      <div ref="scrollBody" class="hale-scroll-body">
        <!-- scaled (never clipped) by fitCurrentPage() when a screen's rows
             still run past the bottom -- see that method for why the packing
             alone can't guarantee a fit. -->
        <div v-if="currentScrollPageData" ref="scrollContent" class="hale-scroll-content" :style="{ transform: 'scale(' + scroll_scale + ')' }">
          <div
              v-for="(section, sIdx) in currentScrollPageData.sections"
              :key="'scroll-section-' + sIdx"
              class="garage-section"
          >
            <div class="garage-header">
              Garage: {{ section.garage.toUpperCase() }} ({{ section.total }}){{ section.continued ? ' — (cont\'d)' : '' }}
            </div>
            <table border="1" class="dispatch-table">
              <tr>
                <th v-for="col in columns" :key="col.key">{{ scrollLabel(col) }}</th>
              </tr>
              <tr v-if="!section.rows.length">
                <td :colspan="columns.length">No trips</td>
              </tr>
              <tr v-for="(row, rIdx) in section.rows" :key="'scroll-row-' + rIdx">
                <td v-for="col in columns" :key="col.key" :class="col.key === 'status' ? statusClass(row.status) : ''">
                  <span :class="{ 'hale-scroll-clamp': isScrollClamped(col) }">{{ scrollCell(row, col, currentScrollPageData.date) }}</span>
                </td>
              </tr>
            </table>
          </div>
        </div>
        <!-- these two wait on the measure pass: until it finishes there are no
             screens yet, and flashing "no trips" on the way in reads as an
             error. an empty range measures to no rows and lands here, which is
             the only way the viewer hears about it -- the overlay has no other
             chrome to explain a blank screen. -->
        <div v-else-if="scroll_measured" class="hale-scroll-empty">No trips found in that range.</div>
        <div v-else class="hale-scroll-empty">Loading dispatch...</div>
      </div>
      <!-- re-keyed per page so the fill animation restarts from empty on every advance -->
      <div v-if="scrollPageCount > 1" class="hale-scroll-progress">
        <div :key="'scroll-progress-' + scroll_page" class="hale-scroll-progress-bar" :style="{ animationDuration: scrollPageSeconds + 's' }"></div>
      </div>

      <!-- off-screen height probe: every row of the range laid out at the same
           width as the display above, so measureScrollRows() can read each
           row's real height before packing. rows vary a lot (an address that
           wraps to three lines is ~3x a one-line row), and only the rows on
           the current screen are otherwise available to measure -- so
           screen-filling has to work from the whole range's real heights
           rather than one sampled number. mounted only during a measure pass. -->
      <div
          v-if="scroll_measuring"
          ref="scrollProbe"
          class="hale-scroll-probe"
          :style="{ width: scroll_probe_width + 'px' }"
      >
        <div
            v-for="(day, dIdx) in scrollRecords"
            :key="'probe-day-' + dIdx"
        >
          <div
              v-for="garage in visibleGarages"
              :key="'probe-section-' + dIdx + '-' + garage"
              class="garage-section"
          >
            <div class="garage-header">Garage: {{ garage.toUpperCase() }}</div>
            <table border="1" class="dispatch-table">
              <tr class="hale-probe-columns">
                <th v-for="col in columns" :key="col.key">{{ scrollLabel(col) }}</th>
              </tr>
              <tr v-if="!((day.garages && day.garages[garage]) || []).length" class="hale-probe-empty">
                <td :colspan="columns.length">No trips</td>
              </tr>
              <tr
                  v-for="(row, rIdx) in ((day.garages && day.garages[garage]) || [])"
                  :key="'probe-row-' + rIdx"
                  :data-probe-row="dIdx + '|' + garage + '|' + rIdx"
              >
                <td v-for="col in columns" :key="col.key" :class="col.key === 'status' ? statusClass(row.status) : ''">
                  <span :class="{ 'hale-scroll-clamp': isScrollClamped(col) }">{{ scrollCell(row, col, day.date) }}</span>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { get_tbn_hale_daily_dispatch, api_url } from '../networking/tbn_apis'
import { mapState } from 'vuex'
import { justDate } from '../helpers/dates'
import moment from 'moment'
import axios from 'axios'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

// how many trip rows are allowed on one printed page, counted across whatever
// garage sections buildPages() packs onto it. deliberately small: Pickup
// Location / Destination hold full addresses that can wrap to 2-3 lines, so
// this assumes something close to worst case and a page's real content can't
// realistically exceed one physical letter/landscape page. printPage()'s
// shrink-to-fit is a safety net for the rare page that still runs long, not
// the main mechanism -- if this number is too high, most pages end up needing
// to shrink, which (since it shrinks proportionally) leaves visible blank
// margins on the sides.
const ROWS_PER_PRINT_PAGE = 5

// the width the print stage renders at, and so the effective px-per-sheet
// scale: each captured page is placed across the sheet's full printable
// width at whatever height it naturally comes out to. it's a starting width
// rather than a fixed one -- widenPrintStageToFit() grows it for a page
// whose columns can't compress this far.
const PRINT_STAGE_WIDTH_PX = 1000
const PRINT_STAGE_PADDING_PX = 10
const PDF_PAGE_WIDTH_IN = 11
const PDF_PAGE_HEIGHT_IN = 8.5
const PDF_MARGIN_IN = 0.25

// the Range starts on today plus the next six days, and follows the day until
// somebody picks their own dates -- see rollDateRange(). the week is what Hale
// asked for; the rolling is what stops a lounge TV showing the week somebody
// set last Tuesday, since there's nobody at the wall to notice it went stale.
const RANGE_DEFAULT_DAYS = 7

// auto-scroll (lounge TV) mode -- kept deliberately simple (no themes, no
// manual next/prev, no per-tenant config) to fit the scope quoted for this.
// bump these if Patrick says it's paging too fast/slow or going stale.
const SCROLL_PAGE_MS = 12000
const SCROLL_REFRESH_MS = 60000
const SCROLL_RESIZE_DEBOUNCE_MS = 150
// floor for the shrink-to-fit safety net -- past this the text is too small
// to read from across a lounge, so let it clip rather than pretend.
const MIN_SCROLL_SCALE = 0.5

// the lounge TV's browser reports roughly 960x540 css px (a 1080p panel at
// 200% zoom), so the base type is sized off viewport height rather than fixed
// px: the board then looks the same whatever screen it lands on.
//
// the divisor lands 12px on that TV, a shade under the 13px it runs today.
// almost all of the density here comes from the copy trimming below rather
// than from type size -- measured on a 960x540 screen, one garage block goes
// 2 trips -> 10 at today's 13px, 12 at 12px, 26 at 10px. 12 buys a whole
// garage on one screen while keeping the letters near their current height;
// going smaller trades legibility across a lounge for density we don't need,
// so it's a one-line change here if Hale ever wants it.
const SCROLL_FONT_DIVISOR = 45
const MIN_SCROLL_FONT_PX = 10
// only a backstop against a freakishly tall viewport, deliberately well above
// what a TV asks for: we don't control the panel's zoom, and the same 1080p
// screen reports 540 css px at 200% and 1080 at 100%. a cap that bit at 1080
// would quietly render the whole board at two-thirds size and pack in rows
// nobody asked to read from across a lounge -- the opposite of the point.
const MAX_SCROLL_FONT_PX = 32

// ---- wall-board text ----------------------------------------------------
// A row is only worth the lines it costs. On the TV every line of wrapped
// text in ONE column makes the whole row taller, so a single four-line
// address was costing every other column three empty lines and holding the
// board to two trips a screen. These trim the display copy only: the report
// page, the printed sheet and the CSV all still carry the full values.

// backend sends times as "MM/DD HH:MM". A screen is a single day and its date
// is already in the title, so the date only earns its space when the leg
// lands on a different day -- an overnight return still reads "09/10 06:23".
const scrollTime = (value, date) => {
  const parts = /^(\d{2})\/(\d{2}) (.+)$/.exec(value)
  if (!parts || !date)
    return value
  const [, month, day, time] = parts
  const iso = String(date).split('-')
  return (iso[1] === month && iso[2] === day) ? time : value
}

// "Madison-Oneida BOCES, 4937 Spring Rd, Verona, NY 13478, USA" ->
// "Madison-Oneida BOCES, Verona, NY". The country is always the same one, the
// zip is for a satnav rather than a wall, and between the site name and the
// street it's the name that tells a driver where they're going -- which is
// also how the Destination column already reads.
const scrollAddress = (value) => {
  const parts = String(value).split(',')
    .map(part => part.trim())
    .filter(part => part && !/^(usa|u\.s\.a\.|us|united states)$/i.test(part))
    .map(part => part.replace(/\s*\d{5}(-\d{4})?$/, '').trim())
    .filter(Boolean)
  if (parts.length <= 3)
    return parts.join(', ')
  return [parts[0], parts[parts.length - 2], parts[parts.length - 1]].join(', ')
}

// "698983 MADISON ONEIDA BOCES" -> "698983". The customer name trails every
// charter number and is the same name the pickup column shows on that row.
const scrollTripRef = (value) => {
  const number = /^(\d+)\b/.exec(String(value))
  return number ? number[1] : value
}

// "WINTERS, DOUGLAS J" -> "WINTERS, D". Three lines for a name nobody reads
// past the surname.
const scrollDriver = (value) => {
  const name = /^([^,]+),\s*(\S)/.exec(String(value))
  return name ? `${name[1].trim()}, ${name[2]}` : value
}

const SCROLL_TIME_COLUMNS = ['driver_start', 'spot_time', 'departure', 'arrival', 'last_drop', 'return_garage']
const SCROLL_ADDRESS_COLUMNS = ['pickup_location', 'destination']
// two-word headers set a column's minimum width, which is width the values
// then don't get. shortened only where the meaning survives it.
const SCROLL_SHORT_LABELS = {
  driver_start: 'Start',
  pickup_location: 'Pickup',
  spot_time: 'Spot',
  return_garage: 'Return',
}

export default {
  name: 'hale_daily_dispatch_report_tbn',

  data() {
    const today = moment()
    const rangeFrom = justDate(today)
    const rangeTo = justDate(moment(today).add(RANGE_DEFAULT_DAYS - 1, 'days'))
    return {
      summary: { garages: [] },
      dayRecords: [],
      printRecords: [],
      // the one printPages[] entry currently rendered in the hidden print
      // stage -- see printPage() below.
      printStagePage: null,
      // widened per page when the columns can't compress into the default
      // stage width, see printPage()
      print_stage_width: PRINT_STAGE_WIDTH_PX,
      loading: true,
      printing: false,
      details_viewDate: justDate(today),
      print_fromDate: rangeFrom,
      print_toDate: rangeTo,
      // the week we last set the Range to ourselves. rollDateRange() compares
      // the pickers against this to tell an untouched default (roll it on) from
      // dates somebody deliberately picked (leave them alone).
      auto_range: { from: rangeFrom, to: rangeTo },
      garage_filter: '',
      // auto-scroll (lounge TV) mode state
      scroll_mode: false,
      scroll_page: 0,
      // measured heights the screens are packed from -- per-row heights keyed
      // "dayIndex|garage|rowIndex", plus the garage band / column row / "No
      // trips" row and the gap between sections. see measureScrollRows().
      scroll_heights: { rows: {}, header: 0, columns: 0, empty: 0, gap: 0 },
      // usable height of one screen in the same px units as scroll_heights
      scroll_avail_h: 0,
      // viewport height the base type is sized from, see scrollFontPx
      scroll_viewport_h: 0,
      // whether the off-screen height probe is currently mounted
      scroll_measuring: false,
      scroll_probe_width: 0,
      // a measure pass has finished for the loaded data, so scrollPages is
      // meaningful and its being empty really does mean "nothing to show"
      scroll_measured: false,
      // the records scroll_heights was measured against, and what the screens
      // are packed from -- NOT scrollRecords. the heights are keyed by
      // position in the range, so packing a freshly-refreshed range against
      // the previous pass's heights mis-sizes every screen; keeping the two
      // in step means a refresh swaps data and heights together and the
      // display just keeps showing the last consistent screens until then.
      scroll_packed: [],
      // shrink-to-fit factor for the current screen, see fitCurrentPage()
      scroll_scale: 1,
      // the fetched range: measured, then promoted to scroll_packed
      scrollRecords: [],
      scroll_page_timer: null,
      scroll_refresh_timer: null,
      scroll_resize_timer: null,
      scroll_did_request_fullscreen: false,
      // order/labels follow the old RBS "Diary and Scheduling" sheet where possible
      // (Driver, On Duty, PickUp, Spot, Destination, Last Drop, Return Garage); Departure
      // and the destination-arrival time aren't on that sheet but are kept as useful additions.
      columns: [
        { key: 'status', label: 'Status' },
        { key: 'vehicle', label: 'Vehicle' },
        { key: 'vehicle_type', label: 'Type' },
        { key: 'trip_ref', label: 'Trip Ref' },
        { key: 'driver', label: 'Driver' },
        { key: 'driver_start', label: 'Driver Start' },
        { key: 'pickup_location', label: 'Pickup Location' },
        { key: 'spot_time', label: 'Spot Time' },
        { key: 'departure', label: 'Departure' },
        { key: 'destination', label: 'Destination' },
        { key: 'arrival', label: 'Arrival' },
        { key: 'last_drop', label: 'Last Drop' },
        { key: 'return_garage', label: 'Return Garage' },
      ],
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(1, 'year'),
        maxDate: moment().add(1, 'year'),
        keepOpen: false,
        showClear: true,
        showClose: true,
        // without this the widget anchors to the viewport instead of the input
        // (common bootstrap-datetimepicker issue elsewhere in this app)
        widgetPositioning: {
          horizontal: 'left',
          vertical: 'bottom',
        },
      },
    }
  },

  async created() {
    this.load_data()
  },

  methods: {
    statusClass(status) {
      return status === 'OUT' ? 'status-out' : 'status-returning'
    },
    async load_data() {
      this.loading = true
      try {
        const day = moment(this.details_viewDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const { value: { data } } = await get_tbn_hale_daily_dispatch(this.user.tenant, day, day, this.garage_filter)
        this.summary = (data && data.summary) ? data.summary : { garages: [] }
        this.dayRecords = (data && data.results) ? data.results : []
      } catch (e) {
        this.$emit('showAlert', 'Error retrieving daily dispatch data. Try logging in again or contact support.', 'danger', 5000)
      }
      this.loading = false
    },
    shift_day(days) {
      this.details_viewDate = moment(this.details_viewDate, 'MM-DD-YYYY').add(days, 'days').format('MM-DD-YYYY')
      this.load_data()
    },
    // pulls the full print date range (every day in one shot, same shape as the
    // single-day live view) so the PDF below can lay out one page per day.
    async loadPrintRange() {
      const from = moment(this.print_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const to = moment(this.print_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const { value: { data } } = await get_tbn_hale_daily_dispatch(this.user.tenant, from, to, this.garage_filter)
      this.printRecords = (data && data.results) ? data.results : []
    },
    // renders each printPages[] entry into the hidden print stage one at a
    // time and captures it as its own html2canvas snapshot -- see the
    // print-stage template comment for why this has to happen per-page
    // rather than all at once.
    async printPage() {
      if (this.printing)
        return
      this.printing = true
      try {
        await this.loadPrintRange()
        const pages = this.printPages
        if (!pages.length) {
          this.$emit('showAlert', 'No trips found in that date range.', 'warning', 5000)
          return
        }

        const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: 'letter' })
        for (let i = 0; i < pages.length; i++) {
          this.print_stage_width = PRINT_STAGE_WIDTH_PX
          this.printStagePage = pages[i]
          await this.$nextTick()
          await this.widenPrintStageToFit()

          const canvas = await html2canvas(this.$refs.printStage, {
            // higher scale = more source pixels per printed inch, so text stays
            // crisp even after a PDF viewer's default fit-to-window zoom
            // downsamples it for display -- 2x was still coming out faint/soft.
            scale: 3,
            useCORS: true,
            backgroundColor: '#ffffff',
          })
          // PNG (lossless), not JPEG -- JPEG's chroma subsampling blurs/lightens
          // thin black text strokes on a white background, which is what was
          // making the printed rows look faint.
          const imgData = canvas.toDataURL('image/png')

          // fit within the page with a margin on all sides, shrinking (never
          // stretching) if needed so a page that runs a bit tall never gets
          // its bottom sliced off by the page edge -- addImage doesn't clip
          // or scale on its own, it just draws at whatever size it's given.
          const availableWidthIn = PDF_PAGE_WIDTH_IN - 2 * PDF_MARGIN_IN
          const availableHeightIn = PDF_PAGE_HEIGHT_IN - 2 * PDF_MARGIN_IN
          const naturalHeightIn = (canvas.height / canvas.width) * availableWidthIn
          const shrink = Math.min(1, availableHeightIn / naturalHeightIn)
          const drawWidthIn = availableWidthIn * shrink
          const drawHeightIn = naturalHeightIn * shrink
          const offsetXIn = PDF_MARGIN_IN + (availableWidthIn - drawWidthIn) / 2

          if (i > 0)
            pdf.addPage('letter', 'landscape')
          // 'FAST' is zlib over the same PNG pixels -- lossless, so it costs
          // nothing in print quality (the faintness we fixed earlier came
          // from JPEG's chroma subsampling, which this isn't). without it
          // jsPDF embeds each 3x capture raw and a single day came out at
          // ~250MB, far too big to email a garage.
          pdf.addImage(imgData, 'PNG', offsetXIn, PDF_MARGIN_IN, drawWidthIn, drawHeightIn, undefined, 'FAST')
        }

        pdf.save(`${this.printFilename}.pdf`)
      } catch (e) {
        this.$emit('showAlert', 'Error preparing print. Try logging in again or contact support.', 'danger', 5000)
      } finally {
        this.printStagePage = null
        this.print_stage_width = PRINT_STAGE_WIDTH_PX
        this.printing = false
      }
    },
    // a table can't lay out narrower than its columns' minimum content, so a
    // page whose rows hold long text in several columns at once (full street
    // addresses in both Pickup Location and Destination, say) overflows the
    // stage's width. html2canvas crops at the stage's own box, which sliced
    // the right-hand columns off those pages -- and it hit "(cont'd)" pages
    // most, since whether a chunk of rows overflows depends on which rows
    // happen to land on it. widening the stage to whatever the table needs
    // keeps the capture whole; the PDF then scales that page to the sheet
    // width like any other, so it just comes out a couple of percent smaller.
    async widenPrintStageToFit() {
      const stage = this.$refs.printStage
      if (!stage)
        return
      const tables = Array.from(stage.querySelectorAll('.dispatch-table'))
      if (!tables.length)
        return
      const needed = Math.ceil(Math.max(...tables.map(t => t.offsetWidth))) + 2 * PRINT_STAGE_PADDING_PX
      if (needed <= this.print_stage_width)
        return
      this.print_stage_width = needed
      await this.$nextTick()
    },
    // print's page packing (auto-scroll keeps the same rules but packs by
    // measured pixels instead -- see packScrollScreens()): garages share a
    // page whenever they both fit within rowsPerPage, and a garage bigger
    // than the budget splits, marked "(cont'd)" on its later chunks.
    buildPages(records, garages, rowsPerPage) {
      const pages = []
      for (const day of records) {
        let page = { date_label: day.date_label, sections: [] }
        let used = 0

        for (const garage of garages) {
          const rows = (day.garages && day.garages[garage]) || []

          if (!rows.length) {
            if (used >= rowsPerPage) {
              pages.push(page)
              page = { date_label: day.date_label, sections: [] }
              used = 0
            }
            page.sections.push({ garage, rows: [], total: 0, continued: false })
            continue
          }

          let idx = 0
          let firstChunk = true
          while (idx < rows.length) {
            if (used >= rowsPerPage) {
              pages.push(page)
              page = { date_label: day.date_label, sections: [] }
              used = 0
            }
            const take = Math.min(rowsPerPage - used, rows.length - idx)
            const chunk = rows.slice(idx, idx + take)
            page.sections.push({ garage, rows: chunk, total: rows.length, continued: !firstChunk })
            used += chunk.length
            idx += chunk.length
            firstChunk = false
          }
        }

        pages.push(page)
      }
      return pages
    },
    // display copy for one cell on the TV board. Used by the screen AND by the
    // measurement probe -- they have to render the same string or every row
    // height is measured against text that isn't what gets shown.
    scrollCell(row, col, date) {
      const value = row[col.key] || ''
      if (SCROLL_TIME_COLUMNS.indexOf(col.key) !== -1)
        return scrollTime(value, date)
      if (SCROLL_ADDRESS_COLUMNS.indexOf(col.key) !== -1)
        return scrollAddress(value)
      if (col.key === 'trip_ref')
        return scrollTripRef(value)
      if (col.key === 'driver')
        return scrollDriver(value)
      return value
    },
    scrollLabel(col) {
      return SCROLL_SHORT_LABELS[col.key] || col.label
    },
    // only the address columns can still run long enough to need the cap
    isScrollClamped(col) {
      return SCROLL_ADDRESS_COLUMNS.indexOf(col.key) !== -1
    },
    // moves the Range on to today's week, so a page (or a TV) left up
    // overnight isn't still showing yesterday's. only ever touches the pickers
    // while they hold the week we put there ourselves -- once somebody picks
    // their own dates those are theirs to keep, and everything here (display,
    // print, CSV) runs on them until they're changed back. returns whether it
    // rolled, since the display has to start the new week at its first screen.
    rollDateRange() {
      const from = justDate(moment())
      const to = justDate(moment().add(RANGE_DEFAULT_DAYS - 1, 'days'))
      if (this.print_fromDate !== this.auto_range.from || this.print_toDate !== this.auto_range.to)
        return false
      if (this.print_fromDate === from && this.print_toDate === to)
        return false
      this.print_fromDate = from
      this.print_toDate = to
      this.auto_range = { from, to }
      return true
    },
    // pulls the same range/garage selected above (shared with print/CSV), so
    // auto-scroll shows whatever the page is configured for -- rolled on
    // first, because this runs every SCROLL_REFRESH_MS under a running
    // display and that's what carries an unattended TV into the new day.
    async loadScrollRange() {
      const rolled = this.rollDateRange()
      const from = moment(this.print_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const to = moment(this.print_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const { value: { data } } = await get_tbn_hale_daily_dispatch(this.user.tenant, from, to, this.garage_filter)
      this.scrollRecords = (data && data.results) ? data.results : []
      // midnight turned under a running display: yesterday has dropped off the
      // front of the week, so the screen it was on now belongs to a different
      // day. start the new week at its first screen -- someone walking past at
      // 12:01am should see today, not the middle of Thursday.
      if (rolled)
        this.scroll_page = 0
    },
    async enterScroll() {
      if (this.scroll_mode)
        return
      this.scroll_mode = true
      this.scroll_page = 0
      this.scroll_measured = false
      // last session's screens (a different range or garage, possibly) would
      // otherwise show until this session's first measure lands
      this.scroll_packed = []
      // a scale left over from the last session would show for a frame
      // before fitCurrentPage() re-measures
      this.scroll_scale = 1
      try {
        await this.loadScrollRange()
      } catch (e) {
        this.$emit('showAlert', 'Error retrieving daily dispatch data. Try logging in again or contact support.', 'danger', 5000)
        this.scroll_mode = false
        return
      }
      // the overlay (Exit and all) is already up during that fetch, and the
      // component can be torn down mid-fetch by a route change -- either way
      // exitScroll() has already run, so attaching listeners and timers now
      // would leave them running with no overlay to drive.
      if (!this.scroll_mode)
        return
      document.addEventListener('keydown', this.onScrollKeydown)
      window.addEventListener('resize', this.onScrollResize)
      document.addEventListener('fullscreenchange', this.onScrollFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this.onScrollFullscreenChange)
      await this.$nextTick()
      await this.measureScrollRows()
      if (!this.scroll_mode)
        return
      this.startScrollTimers()
      this.requestScrollFullscreen()
    },
    exitScroll() {
      if (!this.scroll_mode)
        return
      this.scroll_mode = false
      this.stopScrollTimers()
      document.removeEventListener('keydown', this.onScrollKeydown)
      window.removeEventListener('resize', this.onScrollResize)
      document.removeEventListener('fullscreenchange', this.onScrollFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', this.onScrollFullscreenChange)
      const active = document.fullscreenElement || document.webkitFullscreenElement
      const exit = document.exitFullscreen || document.webkitExitFullscreen
      if (this.scroll_did_request_fullscreen && active && exit) {
        const p = exit.call(document)
        if (p && p.catch) p.catch(() => {})
      }
      this.scroll_did_request_fullscreen = false
    },
    onScrollKeydown(e) {
      if (!this.scroll_mode)
        return
      // 'Esc' for the older browsers a lounge TV might be running
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault()
        this.exitScroll()
      }
    },
    // if the TV/browser exits fullscreen on its own (e.g. someone hits Esc,
    // which the browser intercepts for fullscreen before our keydown handler
    // sees it), fall out of scroll mode too rather than leaving a windowed
    // overlay with nav chrome now visible behind it.
    onScrollFullscreenChange() {
      const active = document.fullscreenElement || document.webkitFullscreenElement
      if (this.scroll_mode && this.scroll_did_request_fullscreen && !active)
        this.exitScroll()
    },
    // prefixed forms included for the same reason as vsm_board.vue's display
    // mode: the browsers on these lounge/kiosk screens aren't all current.
    // failing is fine -- the overlay covers the page on its own, it just
    // leaves the browser's own chrome showing.
    requestScrollFullscreen() {
      const el = this.$refs.scrollOverlay
      if (!el)
        return
      const request = el.requestFullscreen || el.webkitRequestFullscreen
      if (!request)
        return
      try {
        const p = request.call(el)
        if (p && p.catch) p.catch(() => {})
        this.scroll_did_request_fullscreen = true
      } catch (e) {
        // best effort
      }
    },
    startScrollTimers() {
      this.stopScrollTimers()
      this.scroll_page_timer = setInterval(this.advanceScrollPage, SCROLL_PAGE_MS)
      this.scroll_refresh_timer = setInterval(this.refreshScroll, SCROLL_REFRESH_MS)
    },
    stopScrollTimers() {
      if (this.scroll_page_timer) {
        clearInterval(this.scroll_page_timer)
        this.scroll_page_timer = null
      }
      if (this.scroll_refresh_timer) {
        clearInterval(this.scroll_refresh_timer)
        this.scroll_refresh_timer = null
      }
      if (this.scroll_resize_timer) {
        clearTimeout(this.scroll_resize_timer)
        this.scroll_resize_timer = null
      }
    },
    advanceScrollPage() {
      const count = this.scrollPageCount
      if (count <= 1)
        return
      this.scroll_page = (this.scroll_page + 1) % count
      // heights are already known for the whole range, so a screen change
      // needs no re-measure -- just re-check this screen's fit.
      this.fitCurrentPage()
    },
    // re-fetches the selected range every SCROLL_REFRESH_MS so a TV left
    // running unattended stays current, and is where the default week rolls
    // onto a new day -- skipped while the tab/window is hidden, same
    // reasoning as the standalone VSM boards' reload timers.
    async refreshScroll() {
      if (document.hidden)
        return
      try {
        await this.loadScrollRange()
      } catch (e) {
        // keep showing the last good data rather than blanking an unattended TV
        return
      }
      if (!this.scroll_mode)
        return
      // the fetch above only stages the new data (see scroll_packed) -- this
      // is what measures it and puts it on screen. the scrollPageCount
      // watcher keeps scroll_page in range if the range got shorter.
      await this.measureScrollRows()
    },
    onScrollResize() {
      if (this.scroll_resize_timer)
        clearTimeout(this.scroll_resize_timer)
      // every row's height changes with the width, so all of them need
      // re-measuring at the new size
      this.scroll_resize_timer = setTimeout(this.measureScrollRows, SCROLL_RESIZE_DEBOUNCE_MS)
    },
    // measures the real height of every row in the range, so packScrollScreens()
    // can fill each screen to the pixel instead of guessing a rows-per-screen
    // number. print can use a flat row count because its page size never
    // changes and it only has to be roughly right; a TV screen is a fixed
    // amount of space we want actually full, and Hale's rows range from one
    // line to three or four depending on how long the addresses are.
    // also the point where newly fetched data reaches the screen, since that's
    // the moment its heights are known -- see scroll_packed.
    async measureScrollRows() {
      const body = this.$refs.scrollBody
      if (!body || !body.clientHeight)
        return

      // type is sized off the viewport, so it has to land before anything is
      // measured -- every row height and the usable height below depend on it
      this.scroll_viewport_h = window.innerHeight || 0
      await this.$nextTick()
      // probe at the display's own width so text wraps to the same number of
      // lines it will on screen
      this.scroll_probe_width = body.clientWidth
      this.scroll_measuring = true
      await this.$nextTick()

      const probe = this.$refs.scrollProbe
      if (!probe) {
        this.scroll_measuring = false
        return
      }

      const rows = {}
      for (const tr of probe.querySelectorAll('[data-probe-row]'))
        rows[tr.dataset.probeRow] = tr.offsetHeight

      const section = probe.querySelector('.garage-section')
      const header = probe.querySelector('.garage-header')
      const columnsRow = probe.querySelector('.hale-probe-columns')
      const emptyRow = probe.querySelector('.hale-probe-empty')
      const table = probe.querySelector('.dispatch-table')
      // sections are spaced by a margin, which is real screen space the
      // packing would otherwise spend on rows and then have to shrink back out
      const gap = section ? parseFloat(getComputedStyle(section).marginBottom) || 0 : 0

      // all 13 columns need ~870px; where the screen is narrower than that the
      // table can't shrink any further and fitCurrentPage() scales the whole
      // screen down, which frees up vertical space in the same proportion.
      // measuring that here (rather than off the displayed screen) keeps the
      // packing independent of whichever screen happens to be up.
      const widthScale = table && table.offsetWidth > body.clientWidth
        ? Math.max(MIN_SCROLL_SCALE, body.clientWidth / table.offsetWidth)
        : 1

      // heights, budget and the records they describe all land together, so
      // the screens only ever re-pack from one consistent set
      this.scroll_heights = {
        rows,
        header: header ? header.offsetHeight : 0,
        columns: columnsRow ? columnsRow.offsetHeight : 0,
        empty: emptyRow ? emptyRow.offsetHeight : 0,
        gap,
      }
      this.scroll_avail_h = body.clientHeight / widthScale
      this.scroll_packed = this.scrollRecords
      this.scroll_measured = true

      // unmount the probe: it holds the whole range's rows, and leaving it in
      // the DOM would have every screen change re-layout all of them
      this.scroll_measuring = false
      await this.$nextTick()
      this.fitCurrentPage()
    },
    // fills each screen up to its measured pixel budget, keeping the same
    // shape/rules as print's buildPages(): garages share a screen when they
    // fit, and a garage too big for one screen splits with "(cont'd)".
    packScrollScreens(records, garages) {
      const avail = this.scroll_avail_h
      const h = this.scroll_heights
      const sectionCost = h.header + h.columns
      const pages = []

      for (let dIdx = 0; dIdx < records.length; dIdx++) {
        const day = records[dIdx]
        // date rides along with the label so scrollCell() can tell which times
        // are on the screen's own day and can drop their date
        let page = { date_label: day.date_label, date: day.date, sections: [] }
        let used = 0
        const startPage = () => {
          pages.push(page)
          page = { date_label: day.date_label, date: day.date, sections: [] }
          used = 0
        }
        // what it costs to open a garage section here: its band and column
        // row, plus the gap that only exists between sections, so the first
        // section on a screen is cheaper than the ones after it
        const openCost = () => sectionCost + (used > 0 ? h.gap : 0)

        for (const garage of garages) {
          const rows = (day.garages && day.garages[garage]) || []

          if (!rows.length) {
            if (used > 0 && used + openCost() + h.empty > avail)
              startPage()
            page.sections.push({ garage, rows: [], total: 0, continued: false })
            used += openCost() + h.empty
            continue
          }

          let idx = 0
          let firstChunk = true
          while (idx < rows.length) {
            const rowH = (i) => h.rows[`${dIdx}|${garage}|${i}`] || h.columns
            // don't start a section this screen can't show at least one row of
            if (used > 0 && used + openCost() + rowH(idx) > avail)
              startPage()
            used += openCost()

            const chunk = []
            while (idx < rows.length) {
              const next = rowH(idx)
              // always take one row, so a row taller than the whole screen
              // can't spin here forever -- fitCurrentPage() shrinks that case
              if (chunk.length && used + next > avail)
                break
              used += next
              chunk.push(rows[idx])
              idx++
            }

            page.sections.push({ garage, rows: chunk, total: rows.length, continued: !firstChunk })
            firstChunk = false
            if (idx < rows.length)
              startPage()
          }
        }

        pages.push(page)
      }
      return pages
    },
    // shrink-to-fit safety net, same role as printPage()'s shrink. the body
    // clips rather than scrolls, so anything that overflows is silently
    // hidden dispatch data. two things still can, even though the packing
    // works from measured heights:
    //   height -- a row taller than a whole screen has to be placed anyway,
    //             and right after a refresh the heights belong to the
    //             previous data until measureScrollRows() runs again
    //   width  -- all 13 columns need ~870px, which a narrow window or a
    //             smaller TV doesn't have, and the last columns just vanish
    // scaling the screen down by whichever is tighter keeps everything visible.
    fitCurrentPage() {
      if (!this.scroll_mode)
        return
      this.$nextTick(() => {
        const body = this.$refs.scrollBody
        const content = this.$refs.scrollContent
        if (!body || !content)
          return
        // scrollHeight/scrollWidth are layout values, unaffected by the
        // transform already on the element, so this doesn't compound
        const needed = { h: content.scrollHeight, w: content.scrollWidth }
        if (!needed.h || !needed.w || !body.clientHeight || !body.clientWidth)
          return
        const scale = Math.min(1, body.clientHeight / needed.h, body.clientWidth / needed.w)
        this.scroll_scale = Math.max(MIN_SCROLL_SCALE, scale)
      })
    },
    async downloadCsv() {
      this.$emit('showAlert', 'preparing download...', 'info', 3000)
      const from = moment(this.print_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const to = moment(this.print_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const filename = `hale-daily-dispatch-${from}-to-${to}.csv`
      try {
        const response = await axios.post(
          `${api_url}tbn/hale/daily-dispatch/print`,
          {
            tenant: this.user.tenant,
            fromDate: from,
            toDate: to,
            garage: this.garage_filter || '',
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'text/csv',
            },
            responseType: 'text',
          }
        )
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(url)
        link.remove()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      } catch (e) {
        this.$emit('showAlert', 'Download failed.', 'danger')
      }
    },
  },

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    printedAt() {
      return moment().format('MMMM Do YYYY, h:mm a')
    },
    printFilename() {
      const from = moment(this.print_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const to = moment(this.print_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      return `hale-daily-dispatch-${from}-to-${to}`
    },
    printStageWidthPx() {
      return `${this.print_stage_width}px`
    },
    printStagePaddingPx() {
      return `${PRINT_STAGE_PADDING_PX}px`
    },
    garageOptions() {
      const opts = [{ value: '', text: 'All Garages' }]
      for (const g of (this.summary.garages || [])) {
        opts.push({ value: g, text: g })
      }
      return opts
    },
    // driven by summary.garages (from the backend's GARAGES map) rather than hardcoded
    // names, so this stays accurate if Hale adds/renames a garage.
    garageListLabel() {
      const garages = this.summary.garages || []
      return garages.length ? ` (${garages.join(' / ')})` : ''
    },
    visibleGarages() {
      const all = this.summary.garages || []
      return this.garage_filter ? all.filter(g => g === this.garage_filter) : all
    },
    garageRows() {
      const day = this.dayRecords[0]
      return (day && day.garages) ? day.garages : {}
    },
    // one entry per printed page: { date_label, sections: [{ garage, rows, total, continued }] }.
    // packs sections (across garages) onto a page up to ROWS_PER_PRINT_PAGE
    // rows total, only starting a new page once the current one is full --
    // a light garage (few trips) shares the page with the next garage's
    // rows instead of getting its own mostly-empty page. a garage with more
    // rows than fit in the remaining budget just splits across pages, same
    // as before, marked "(cont'd)" on its later chunks.
    printPages() {
      return this.buildPages(this.printRecords, this.visibleGarages, ROWS_PER_PRINT_PAGE)
    },
    // same shape as printPages, but packed to the screen's measured pixel
    // budget rather than a row count -- see packScrollScreens().
    scrollPages() {
      // nothing to pack against until a measure pass has run, and an empty
      // range has no section to measure at all (heights stay zero), which
      // would otherwise put every row on its own screen
      if (!this.scroll_avail_h || !this.scroll_heights.columns)
        return []
      const pages = this.packScrollScreens(this.scroll_packed, this.visibleGarages)
      // a garage with no trips rides along fine on a screen that has other
      // trips (print keeps those "No trips" bands, and they're informative),
      // but a screen holding nothing else would hog a full 12s slot on the
      // TV showing an empty band. drop those -- unless it's a day's only
      // screen, where "no trips" is the honest answer for a quiet day.
      const screensPerDay = {}
      for (const page of pages)
        screensPerDay[page.date_label] = (screensPerDay[page.date_label] || 0) + 1
      return pages.filter(page =>
        screensPerDay[page.date_label] === 1 || page.sections.some(s => s.rows.length)
      )
    },
    scrollPageCount() {
      return this.scrollPages.length
    },
    currentScrollPageData() {
      return this.scrollPages[this.scroll_page] || null
    },
    scrollPageSeconds() {
      return SCROLL_PAGE_MS / 1000
    },
    // everything in the overlay is sized in em off this, so the whole board
    // scales with the screen instead of the type staying put while the screen
    // grows. deliberately smaller than the report page's: on a wall the
    // constraint is trips visible, and physical letter size is carried by the
    // panel being 60 inches wide rather than by the px count.
    scrollFontPx() {
      if (!this.scroll_viewport_h)
        return MIN_SCROLL_FONT_PX
      const sized = Math.round(this.scroll_viewport_h / SCROLL_FONT_DIVISOR)
      return Math.max(MIN_SCROLL_FONT_PX, Math.min(MAX_SCROLL_FONT_PX, sized))
    },
  },

  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    },
    // a refresh or a re-measure re-packs every screen, which can leave
    // scroll_page pointing past the end -- without this the overlay would sit
    // on its empty state until the next advance came around.
    scrollPageCount(count) {
      if (this.scroll_page >= count)
        this.scroll_page = count > 0 ? count - 1 : 0
    },
  },

  beforeDestroy() {
    // in case the user navigates away while auto-scroll is still running --
    // exitScroll() already removes the listeners/timers it added, this just
    // guarantees it happens even if the component is torn down mid-display.
    this.exitScroll()
  },
}
</script>

<style scoped>
.white-link { font-size: 22px; font-weight: bold; color: white !important; }
.garage-section { margin-bottom: 20px; }
.garage-header {
  background: #333;
  color: #fff;
  font-size: 15px;
  font-weight: bold;
  padding: 6px 10px;
  margin-bottom: 4px;
  text-align: center;
}
.pdf-page-title {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
}
.dispatch-table {
  border-collapse: collapse;
  font-size: 13px;
  width: 100%;
}
.dispatch-table th,
.dispatch-table td {
  border: 1px solid #999;
  padding: 6px 8px;
  text-align: left;
}
.dispatch-table th {
  background-color: #F9F8F8;
  font-weight: bold;
}
.status-out { color: #0a6b0a; font-weight: bold; }
.status-returning { color: #a35400; font-weight: bold; }

/* auto-scroll (lounge TV) overlay */
.hale-scroll-overlay {
  position: fixed;
  inset: 0;
  /* same as vsm_board/departure_board's display overlays, well clear of the
     app chrome (1000) so nothing shows through when fullscreen is refused */
  z-index: 4000;
  background: #fff;
  color: #000;
  display: flex;
  flex-direction: column;
  /* px, not em: the probe measures against this width and shouldn't move when
     the base type resizes */
  padding: 10px 16px;
}
/* everything below is sized in em off the overlay's viewport-derived base
   (see scrollFontPx), so the board keeps its proportions on any screen. it
   runs tighter than the report page on purpose -- the page is read from two
   feet away, this is read from across a lounge and every line of chrome is a
   trip that doesn't fit. */
.hale-scroll-overlay .dispatch-table { font-size: 1em; }
.hale-scroll-overlay .dispatch-table th,
.hale-scroll-overlay .dispatch-table td { padding: 0.2em 0.45em; }
.hale-scroll-overlay .garage-header {
  font-size: 1.05em;
  padding: 0.25em 0.7em;
  margin-bottom: 0.2em;
}
.hale-scroll-overlay .garage-section { margin-bottom: 0.9em; }
/* the cap Patrick asked for. only the addresses can still run long, and past
   three lines one stop is squeezing out a whole trip. */
.hale-scroll-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.hale-scroll-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6em;
}
.hale-scroll-title { font-size: 1.9em; font-weight: bold; }
.hale-scroll-controls { display: flex; align-items: center; gap: 1.4em; font-size: 1.1em; }
.hale-scroll-count { opacity: 0.65; }
.hale-scroll-exit { font-weight: bold; color: inherit; text-decoration: underline; }
.hale-scroll-body {
  flex: 1 1 auto;
  overflow: hidden;
}
.hale-scroll-content {
  transform-origin: top center;
}
/* laid out (so it can be measured) but off-screen and inert */
.hale-scroll-probe {
  position: fixed;
  left: -99999px;
  top: 0;
  visibility: hidden;
  pointer-events: none;
}
.hale-scroll-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 1.6em;
  opacity: 0.6;
}
/* thin bar filling over SCROLL_PAGE_MS to show when the next screen is coming */
.hale-scroll-progress {
  flex: 0 0 auto;
  height: 4px;
  background: #ddd;
  margin-top: 0.6em;
  overflow: hidden;
}
.hale-scroll-progress-bar {
  height: 100%;
  width: 0%;
  background: #333;
  animation-name: hale-dispatch-scroll-progress-fill;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
@keyframes hale-dispatch-scroll-progress-fill {
  from { width: 0%; }
  to { width: 100%; }
}
</style>
